/**
 * @krisspy-file
 * @type page
 * @name "ProductsList"
 * @title "Products List"
 * @description "View and manage all rental products"
 * @routes ["/products"]
 * @layout "DashboardLayout"
 * @flowName "product-management"
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Upload,
  Download,
  Package,
  MapPin,
  AlertCircle,
  Loader2,
  EyeOff
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getProducts } from '../services/products.service';
import type { Product } from '../services/products.service';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { initScrollFix, cleanupScrollFix } from '../lib/scrollFix';
import { useUrlFilters } from '../lib/useUrlFilters';

export default function ProductsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { defaultLanguage } = useLanguage();
  const { merchantId, locationIds, isPlatformAdmin, loading: authLoading } = useAuth();

  // Use URL-based filters for persistence across navigation
  const { getFilter, setFilter } = useUrlFilters({
    defaults: {
      search: '',
      category: 'all',
      status: 'all',
      location: 'all'
    },
    storageKey: 'products-filters'
  });

  // Derive filter values from URL
  const searchQuery = getFilter('search');
  const categoryFilter = getFilter('category') || 'all';
  const statusFilter = getFilter('status') || 'all';
  const locationFilter = getFilter('location') || 'all';

  // Setter functions that update URL
  const setSearchQuery = (value: string) => setFilter('search', value);
  const setCategoryFilter = (value: string) => setFilter('category', value);
  const setStatusFilter = (value: string) => setFilter('status', value);
  const setLocationFilter = (value: string) => setFilter('location', value);

  // State for real data
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [categories, setCategories] = useState<string[]>(['all']);
  const [retryCount, setRetryCount] = useState(0);

  // Load locations once (independent of product filters) so the dropdown
  // never loses options after remount, navigation, or re-login.
  useEffect(() => {
    if (authLoading) return;

    async function loadLocations() {
      let query = supabase
        .from('locations')
        .select('id, location_name')
        .order('location_name');

      // Non-platform-admin users should only see their accessible locations
      if (!isPlatformAdmin && merchantId) {
        query = query.eq('merchant_id', merchantId);
      }

      const { data } = await query;
      if (data) {
        setLocations(data.map(loc => ({ id: loc.id, name: loc.location_name })));
      }
    }

    loadLocations();
  }, [authLoading, merchantId, isPlatformAdmin]);

  // Load products when auth is ready, filters change, or returning from delete
  useEffect(() => {
    // Don't fetch until auth has finished loading - merchantId would be null
    // and applyAccessControl would match against a dummy UUID, returning 0 products
    if (authLoading) return;

    let cancelled = false;

    async function loadProducts() {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getProducts(
        { merchantId, locationIds, isPlatformAdmin },
        {
          locationId: locationFilter !== 'all' ? locationFilter : undefined,
          active: statusFilter === 'all' ? undefined : statusFilter === 'active',
          searchQuery: searchQuery || undefined,
        },
        defaultLanguage,
        defaultLanguage
      );

      // If a newer fetch was triggered, discard this stale result
      if (cancelled) return;

      if (fetchError) {
        setError(fetchError);
        setLoading(false);
        return;
      }

      if (data) {
        setProducts(data);

        // Extract unique categories from product results
        const uniqueCategories = Array.from(new Set(data.map((p: any) => p.category_name).filter(Boolean))) as string[];
        setCategories(['all', ...uniqueCategories]);
      }

      setLoading(false);
    }

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [authLoading, merchantId, locationIds, isPlatformAdmin, defaultLanguage, location.state?.reload, statusFilter, locationFilter, retryCount]);

  // Initialize scroll fix for tables
  useEffect(() => {
    // Wait for DOM to render tables
    const timer = setTimeout(() => {
      initScrollFix();
    }, 100);

    return () => {
      clearTimeout(timer);
      cleanupScrollFix();
    };
  }, [products]);

  // Filter products locally for better UX (already filtered by API)
  const filteredProducts = products.filter((product) => {
    const matchesSearch = !searchQuery ||
      product.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = categoryFilter === 'all' || product.category_name === categoryFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' ? product.active : !product.active);
    const matchesLocation = locationFilter === 'all' || product.location_id === locationFilter;

    return matchesSearch && matchesCategory && matchesStatus && matchesLocation;
  });

  const getStatusBadge = (active: boolean | null) => {
    return active ? 'badge-success' : 'badge-danger';
  };

  const exportToCSV = async () => {
    if (filteredProducts.length === 0) return;

    // Re-fetch available_location_ids directly from product_types
    // to avoid any data transformation by getProducts()
    const productIds = filteredProducts.map(p => p.id);
    const availLocMap = new Map<string, string[]>();
    if (productIds.length > 0) {
      const { data: rawProducts } = await supabase
        .from('product_types')
        .select('id, available_location_ids')
        .in('id', productIds);
      if (rawProducts) {
        for (const rp of rawProducts) {
          if (rp.available_location_ids && Array.isArray(rp.available_location_ids) && rp.available_location_ids.length > 0) {
            availLocMap.set(rp.id, rp.available_location_ids);
          }
        }
      }
    }

    // Fetch all locations for name resolution
    const locationMap = new Map<string, string>();
    const { data: allLocs } = await supabase
      .from('locations')
      .select('id, location_name');
    if (allLocs) {
      for (const loc of allLocs) locationMap.set(loc.id, loc.location_name);
    }

    const getAvailableLocationNames = (product: Product): string => {
      const ids = availLocMap.get(product.id);
      if (!ids || ids.length === 0) return '';
      return ids
        .map(id => locationMap.get(id) || id)
        .join(' - ');
    };

    const headers = [
      'Product Type ID',
      'Product Name',
      'Description',
      'Product Type',
      'Category',
      'Location',
      'Status',
      'Inventory (Total)',
      'Inventory (Available)',
      'Model',
      'Featured',
      'Display Order',
      'Inventory Type',
      'Pool Quantity',
      'Maintenance Time (Hours)',
      'Pre-booking Hours',
      'Cutoff Days Prior',
      'Cutoff Time',
      'Available Location IDs (Names)',
    ];

    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const rows = filteredProducts.map((product) => [
      product.id || '',
      product.product_name || '',
      product.description || '',
      product.product_type || '',
      product.category_name || '',
      product.location_name || '',
      product.active ? 'Active' : 'Inactive',
      (product.inventory_count || 0).toString(),
      (product.available_count || 0).toString(),
      product.model || '',
      product.featured ? 'Yes' : 'No',
      product.display_order?.toString() || '',
      product.inventory_type || '',
      product.pool_quantity?.toString() || '',
      (product.maintenance_time_hours ?? '').toString(),
      (product.pre_booking_hours ?? '').toString(),
      (product.cutoff_days_prior ?? '').toString(),
      product.cutoff_time || '',
      getAvailableLocationNames(product),
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get default image based on product type
  const getProductImage = (product: Product) => {
    if (product.picture_url) return product.picture_url;

    // Fallback images based on product type
    const typeImages: Record<string, string> = {
      'bike': 'https://images.unsplash.com/photo-1576435728678-68d0fbf94e91?w=200&h=200&fit=crop',
      'kayak': 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=200&h=200&fit=crop',
      'tent': 'https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=200&h=200&fit=crop',
      'ski': 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?w=200&h=200&fit=crop',
      'surfboard': 'https://images.unsplash.com/photo-1502933691298-84fc14542831?w=200&h=200&fit=crop',
    };

    return typeImages[product.product_type] || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop';
  };

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-sm">{t('products.title')}</h1>
          <p className="text-sm sm:text-base text-secondary">{t('products.description')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm sm:gap-md">
          <button
            onClick={() => alert('Bulk import functionality')}
            className="btn btn-ghost text-sm"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.import')}</span>
          </button>
          <button
            onClick={exportToCSV}
            className="btn btn-ghost text-sm"
            disabled={loading || filteredProducts.length === 0}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t('common.export')}</span>
          </button>
          <button
            onClick={() => navigate('/products/create')}
            className="btn btn-primary text-sm"
          >
            <Plus className="w-4 h-4" />
            {t('products.newProduct')}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="card bg-danger/10 border border-danger/20">
          <div className="flex items-center gap-md">
            <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-danger mb-xs">{t('products.errorLoading')}</h4>
              <p className="text-sm text-danger/80">{error}</p>
            </div>
            <button
              onClick={() => setRetryCount(c => c + 1)}
              className="btn btn-ghost text-danger hover:bg-danger/10"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-lg">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                type="text"
                placeholder={t('products.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="w-full lg:w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? t('products.allCategories') : cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Filter */}
          <div className="w-full lg:w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="all">{t('products.allStatus')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>

          {/* Location Filter */}
          <div className="w-full lg:w-48">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="input pl-10 w-full appearance-none"
              >
                <option value="all">{t('products.allLocations')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('products.loadingProducts')}</span>
          </div>
        </div>
      )}

      {/* Products Table */}
      {!loading && (
        <div className="card overflow-hidden">
          {filteredProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary border-b border-primary">
                  <tr>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.product')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.category')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.type')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.status')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.inventory')}</th>
                    <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('products.location')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      onClick={() => navigate(`/products/${product.id}/edit`)}
                      className="hover:bg-secondary transition-colors cursor-pointer"
                    >
                      <td className="py-md px-lg">
                        <div className="flex items-center gap-md">
                          <img
                            src={getProductImage(product)}
                            alt={product.product_name}
                            className="w-12 h-12 rounded-md object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop';
                            }}
                          />
                          <div>
                            <p className="text-sm font-medium text-primary">{product.product_name}</p>
                            <p className="text-xs text-secondary line-clamp-1">
                              {product.description || t('products.noDescription')}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-md px-lg">
                        {product.category_name ? (
                          <span className="badge badge-info">{product.category_name}</span>
                        ) : (
                          <span className="text-xs text-secondary">{t('products.uncategorized')}</span>
                        )}
                      </td>
                      <td className="py-md px-lg">
                        <span className="text-sm text-secondary capitalize">{product.product_type}</span>
                      </td>
                      <td className="py-md px-lg">
                        <span className={`badge ${getStatusBadge(product.active)}`}>
                          {product.active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="py-md px-lg">
                        <div className="flex flex-col gap-xs">
                          <div className="flex items-center gap-xs">
                            <Package className="w-4 h-4 text-secondary" />
                            <span className={`text-sm font-medium ${
                              (product.inventory_count || 0) === 0 ? 'text-danger' : 'text-primary'
                            }`}>
                              {product.inventory_count || 0} {t('products.total')}
                            </span>
                          </div>
                          {product.available_count !== undefined && (
                            <span className="text-xs text-success flex items-center gap-xs">
                              {product.available_count} {t('products.available')}
                              {product.hide_stock_count && (
                                <span
                                  className="inline-flex text-secondary"
                                  title={t('products.hideStockCountBadge')}
                                  aria-label={t('products.hideStockCountBadge')}
                                  data-testid={`product-hide-stock-count-${product.id}`}
                                >
                                  <EyeOff className="w-3.5 h-3.5" aria-hidden="true" />
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-md px-lg">
                        <div className="flex items-center gap-xs">
                          <MapPin className="w-4 h-4 text-secondary" />
                          <span className="text-sm text-secondary">
                            {product.location_name || t('common.unknown')}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-2xl">
              <div className="w-16 h-16 rounded-full bg-tertiary mx-auto mb-lg flex items-center justify-center">
                <Package className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-lg font-semibold text-primary mb-sm">{t('products.noProductsFound')}</h3>
              <p className="text-secondary text-sm mb-xl">
                {error ? t('products.unableToLoad') : t('products.tryAdjustingFilters')}
              </p>
              <button
                onClick={() => navigate('/products/create')}
                className="btn btn-primary"
              >
                <Plus className="w-4 h-4" />
                {t('products.newProduct')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results Summary */}
      {!loading && filteredProducts.length > 0 && (
        <div className="flex items-center justify-between text-sm text-secondary">
          <p>
            {t('common.showing')} <span className="font-medium text-primary">{filteredProducts.length}</span> {t('common.of')}{' '}
            <span className="font-medium text-primary">{products.length}</span> {t('products.products')}
          </p>
          <p>
            {t('products.totalInventory')}: <span className="font-medium text-primary">
              {filteredProducts.reduce((sum, p) => sum + (p.inventory_count || 0), 0)}
            </span> {t('common.items')}
          </p>
        </div>
      )}
    </div>
  );
}
